// External dependencies
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

// Internal dependencies
import Avatar from '@/modules/users/client/components/Avatar.component';
import { distanceKm, formatDistance, formatPrice } from '../utils/results';

// Price types other than the nightly one, shown as a breakdown
const PRICE_PERIODS = {
  daily: 'night',
  weekly: 'week',
  monthly: 'month',
};

/**
 * One host in the search results: who they are, where, and what a stay costs.
 */
export default function SearchResultCard({
  offer,
  center,
  nights,
  isSelected,
}) {
  const { t } = useTranslation('search');
  const { user } = offer;
  const profileUrl = `/profile/${user.username}`;
  const distance = distanceKm(center, offer.location);
  const prices = offer.prices || [];
  const canHost = offer.status === 'yes';

  const periodLabels = {
    night: t('/ night'),
    week: t('/ week'),
    month: t('/ month'),
  };

  function renderNightlyPrice() {
    if (offer.nightlyFrom === null || offer.nightlyFrom === undefined) {
      return (
        <span className="search-result-card-price-main text-muted">
          {t('Price on request')}
        </span>
      );
    }

    if (offer.nightlyFrom === 0) {
      return <span className="search-result-card-price-main">{t('Free')}</span>;
    }

    return (
      <span className="search-result-card-price-main">
        <span className="search-result-card-price-from">{t('From')} </span>
        <strong>{formatPrice(offer.nightlyFrom, offer.currency)}</strong>{' '}
        {periodLabels.night}
      </span>
    );
  }

  return (
    <article
      className={classnames('search-result-card panel', {
        'panel-default': !isSelected,
        'panel-primary is-selected': isSelected,
      })}
      id={`search-result-${offer._id}`}
      data-testid="search-result-card"
    >
      <div className="panel-body">
        <div className="search-result-card-avatar">
          <a href={profileUrl} tabIndex="-1" aria-hidden="true">
            <Avatar user={user} size={64} link={false} />
          </a>
        </div>
        <div className="search-result-card-body">
          <h4 className="search-result-card-title">
            <a href={profileUrl}>{user.displayName}</a>{' '}
            <small className="text-muted">@{user.username}</small>
          </h4>

          <div className="search-result-card-meta">
            <span
              className={classnames('label', {
                'btn-offer-hosting-yes': canHost,
                'btn-offer-hosting-maybe': !canHost,
              })}
            >
              {canHost ? t('Can host') : t('Might be able to host')}
            </span>
            {offer.maxGuests > 0 && (
              <span className="search-result-card-meta-item">
                {t('Up to {{count}} guests', { count: offer.maxGuests })}
              </span>
            )}
            {distance !== null && (
              <span className="search-result-card-meta-item">
                {t('{{distance}} km from centre', {
                  distance: formatDistance(distance),
                })}
              </span>
            )}
            {user.locationLiving && (
              <span className="search-result-card-meta-item">
                {user.locationLiving}
              </span>
            )}
          </div>

          {offer.description && (
            <p className="search-result-card-excerpt">{offer.description}</p>
          )}

          <div className="search-result-card-price">
            {renderNightlyPrice()}
            {prices.length > 1 && (
              <span className="search-result-card-price-breakdown text-muted">
                {prices
                  .filter(price => PRICE_PERIODS[price.type])
                  .map(
                    price =>
                      `${formatPrice(price.price, offer.currency)} ${
                        periodLabels[PRICE_PERIODS[price.type]]
                      }`,
                  )
                  .join(' · ')}
              </span>
            )}
            {nights > 0 &&
              offer.stayTotal !== null &&
              offer.stayTotal !== undefined && (
                <span className="search-result-card-price-total">
                  {offer.stayTotal === 0
                    ? t('Free for your {{count}} nights', { count: nights })
                    : t('{{total}} total for {{count}} nights', {
                        total: formatPrice(offer.stayTotal, offer.currency),
                        count: nights,
                      })}
                </span>
              )}
          </div>

          <div className="search-result-card-actions">
            <a className="btn btn-sm btn-default" href={profileUrl}>
              {t('View profile')}
            </a>
            <a
              className="btn btn-sm btn-primary"
              href={`/messages/${user.username}`}
            >
              {t('Send a message')}
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

SearchResultCard.propTypes = {
  offer: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    status: PropTypes.string,
    maxGuests: PropTypes.number,
    description: PropTypes.string,
    location: PropTypes.arrayOf(PropTypes.number),
    prices: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        price: PropTypes.number,
      }),
    ),
    currency: PropTypes.string,
    nightlyFrom: PropTypes.number,
    stayTotal: PropTypes.number,
    user: PropTypes.shape({
      username: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      locationLiving: PropTypes.string,
    }).isRequired,
  }).isRequired,
  center: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  nights: PropTypes.number,
  isSelected: PropTypes.bool,
};
